/**
 * Core API request functions
 * Base functions for making authenticated and unauthenticated requests
 */
import logger from '../utils/logger';

function getBaseApiUrlV2() {
    const isLocalhost = window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';

    return isLocalhost
        ? 'http://localhost:8085/api/v2/'
        : 'https://api.nanotradingcopilot.com/api/v2/';
}

/**
 * Get the API origin (without /api/v2/ suffix) for webhook URLs
 */
export function getApiOrigin() {
    const isLocalhost = window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';

    return isLocalhost
        ? 'http://localhost:8085'
        : 'https://api.nanotradingcopilot.com';
}

export async function postRequestBackendDataV2(auth, callback, endpoint, data, method = 'POST') {
    if (typeof callback !== 'function') {
        logger.error('Callback is not a function in postRequestBackendDataV2');
        return;
    }

    const idToken = await auth.currentUser.getIdToken(false);
    const url = getBaseApiUrlV2();

    try {
        const response = await fetch(url + endpoint, {
            method: method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: data ? JSON.stringify(data) : undefined
        });

        // For 4xx errors, try to parse response and return to callback (don't throw)
        // This is important for login flow where 403 contains errorCode for alpha
        if (response.status === 401 || response.status === 403) {
            try {
                const errorData = await response.json();
                // Check for alpha access error on 401
                if (response.status === 401) {
                    const errorMessage = errorData.message || errorData.error || '';
                    if (errorMessage.toLowerCase().includes('alpha access')) {
                        logger.warn('Alpha access required - dispatching event');
                        window.dispatchEvent(new CustomEvent('alpha-access-denied'));
                    }
                }
                // Return the parsed error response to callback
                callback({ ...errorData, httpStatus: response.status });
                return;
            } catch (e) {
                // If JSON parsing fails, throw the HTTP error
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        }

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Handle 204 No Content
        if (response.status === 204) {
            callback(null);
            return;
        }

        const text = await response.text();
        const responseData = text ? JSON.parse(text) : {};
        callback(responseData);
    } catch (error) {
        logger.error('Error in postRequestBackendDataV2:', error);
        callback({ error: error.message });
    }
}

export async function getRequestBackendDataV2NoAuth(callback, path) {
    let url = getBaseApiUrlV2();
    const response = await fetch(url + path, {
        method: 'GET',
    });
    const data = await response.text();
    callback(data);
}

export async function getRequestBackendDataV2(auth, callback, path) {
    // Check if auth and currentUser exist
    if (!auth?.currentUser) {
        callback({ error: "Not authenticated" });
        return;
    }

    try {
        const idToken = await auth.currentUser.getIdToken(false);
        let url = getBaseApiUrlV2();
        const response = await fetch(url + path, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
            }
        });

        // For 4xx errors, try to parse response and return to callback
        if (response.status === 401 || response.status === 403) {
            try {
                const errorData = await response.json();
                // Check for alpha access error on 401
                if (response.status === 401) {
                    const errorMessage = errorData.message || errorData.error || '';
                    if (errorMessage.toLowerCase().includes('alpha access')) {
                        logger.warn('Alpha access required - dispatching event');
                        window.dispatchEvent(new CustomEvent('alpha-access-denied'));
                    }
                }
                callback({ ...errorData, httpStatus: response.status, error: errorData.error || errorData.message || 'Access denied' });
                return;
            } catch (e) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        }

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Handle 204 No Content
        if (response.status === 204) {
            callback(null);
            return;
        }

        const text = await response.text();
        const data = text ? JSON.parse(text) : null;
        logger.log("data", path, data);
        callback(data);
    } catch (error) {
        logger.error("API request error:", error);
        // Only report as authentication failed if it likely was one (e.g. getIdToken failed)
        // Otherwise pass the actual error message
        const isAuthError = error.code && (error.code.startsWith('auth/') || error.message.includes('token'));
        callback({ error: isAuthError ? "Authentication failed" : error.message });
    }
}

export async function deleteRequestBackendDataV2(auth, callback, endpoint) {
    if (typeof callback !== 'function') {
        logger.error('Callback is not a function in deleteRequestBackendDataV2');
        return;
    }

    const idToken = await auth.currentUser.getIdToken(false);
    const url = getBaseApiUrlV2();

    try {
        const response = await fetch(url + endpoint, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Handle 204 No Content
        if (response.status === 204) {
            callback({ success: true });
            return;
        }

        const responseData = await response.json();
        callback(responseData);
    } catch (error) {
        logger.error('Error in deleteRequestBackendDataV2:', error);
        callback({ error: error.message });
    }
}

export function getBaseApiUrl() {
    return getBaseApiUrlV2();
}

